// 调度层：统一验证 AI 工具调用数据格式（纯逻辑，无副作用）。

import type { ToolValidationError, SchedulerResult, ExecuteAction } from '../types';

interface EntityField {
  key: string;
  label: string;
  type: string;
  requiredForCreate: boolean;
  description: string;
}

interface EntitySchema {
  target: ExecuteAction['target'];
  label: string;
  fields: EntityField[];
  supportedActions: Array<'create' | 'update' | 'delete'>;
  notes?: string[];
}

const ENTITY_SCHEMAS: EntitySchema[] = [
  {
    target: 'character',
    label: '人物',
    supportedActions: ['create', 'update', 'delete'],
    fields: [
      { key: 'name', label: '名称', type: 'string', requiredForCreate: true, description: '人物名称' },
      { key: 'age', label: '年龄', type: 'string | null', requiredForCreate: false, description: '年龄（可为空字符串或 null）' },
      { key: 'faction_id', label: '所属势力ID', type: 'string | null', requiredForCreate: false, description: '所属势力的数据库 ID（UUID，可为 null）' },
      { key: 'strength', label: '战力等级', type: 'string | null', requiredForCreate: false, description: '战力描述（可为 null）' },
      { key: 'description', label: '描述', type: 'string', requiredForCreate: false, description: '人物详细描述' },
    ],
  },
  {
    target: 'faction',
    label: '势力',
    supportedActions: ['create', 'update', 'delete'],
    fields: [
      { key: 'name', label: '名称', type: 'string', requiredForCreate: true, description: '势力名称' },
      { key: 'parent_faction_id', label: '父势力ID', type: 'string | null', requiredForCreate: false, description: '父势力的数据库 ID（UUID，可为 null）' },
      { key: 'description', label: '描述', type: 'string', requiredForCreate: false, description: '势力详细描述' },
    ],
  },
  {
    target: 'setting',
    label: '世界设定',
    supportedActions: ['create', 'update', 'delete'],
    fields: [
      { key: 'title', label: '标题', type: 'string', requiredForCreate: true, description: '设定标题' },
      { key: 'content', label: '内容', type: 'string', requiredForCreate: false, description: '设定详细内容' },
    ],
  },
  {
    target: 'item',
    label: '物品',
    supportedActions: ['create', 'update', 'delete'],
    fields: [
      { key: 'name', label: '名称', type: 'string', requiredForCreate: true, description: '物品名称' },
      { key: 'type', label: '类型', type: 'string', requiredForCreate: false, description: '物品类型（如"武器"、"道具"、"秘籍"等，默认"道具"）' },
      { key: 'description', label: '描述', type: 'string', requiredForCreate: false, description: '物品详细描述' },
    ],
  },
  {
    target: 'volume',
    label: '分卷',
    supportedActions: ['create', 'delete'],
    fields: [
      { key: 'title', label: '标题', type: 'string', requiredForCreate: true, description: '分卷标题' },
      { key: 'description', label: '描述', type: 'string', requiredForCreate: false, description: '分卷描述' },
    ],
  },
  {
    target: 'outline',
    label: '故事大纲',
    supportedActions: ['update'],
    notes: ['大纲仅支持 update 操作（整个作品只有一个大纲，通过 update 覆盖更新）'],
    fields: [
      { key: 'content', label: '大纲内容', type: 'string', requiredForCreate: false, description: '完整的故事大纲文本（update 时必需）' },
    ],
  },
  {
    target: 'chapter_content',
    label: '章节内容',
    supportedActions: ['update'],
    notes: [
      '章节内容仅支持 update 操作，必须提供 targetId（章节数据库 ID）',
      'data.content 为章节正文',
      '可选 data.title 更新章节标题、data.summary 更新摘要',
      '所有章节内容变更统一通过审批面板确认后写入数据库',
    ],
    fields: [
      { key: 'content', label: '章节正文', type: 'string', requiredForCreate: false, description: '章节完整正文内容' },
      { key: 'title', label: '章节标题', type: 'string', requiredForCreate: false, description: '章节标题（可选，不传则保留原标题）' },
      { key: 'summary', label: '章节摘要', type: 'string', requiredForCreate: false, description: '章节摘要（可选，不传则保留原摘要）' },
    ],
  },
  {
    target: 'chapter',
    label: '章节',
    supportedActions: ['create'],
    notes: [
      '章节仅支持 create 操作，用于在当前卷中新增章节',
      'data.volume_id 为必填，指定所属卷的数据库 ID',
      '可选 data.content 填写初始正文、data.summary 填写摘要',
      '可选 data.after_chapter_number 指定插入位置（章节编号之后），默认追加到卷末',
    ],
    fields: [
      { key: 'volume_id', label: '所属卷ID', type: 'string', requiredForCreate: true, description: '所属卷的数据库 ID（UUID，通过 query scope=volume 获取）' },
      { key: 'title', label: '章节标题', type: 'string', requiredForCreate: true, description: '章节标题' },
      { key: 'content', label: '章节正文', type: 'string', requiredForCreate: false, description: '章节初始正文内容（可选，默认为空）' },
      { key: 'summary', label: '章节摘要', type: 'string', requiredForCreate: false, description: '章节摘要（可选，默认为空）' },
      { key: 'after_chapter_number', label: '插入位置', type: 'number', requiredForCreate: false, description: '插入到指定章节编号之后（可选，默认追加到卷末）' },
    ],
  },
];

const VALID_ACTION_TYPES = new Set<string>(['create', 'update', 'delete']);

function findSchema(target: string): EntitySchema | undefined {
  return ENTITY_SCHEMAS.find((s) => s.target === target);
}

function makeError(code: string, message: string, dataPath?: string, expectedFormat?: string): ToolValidationError {
  return {
    code,
    message,
    helpSuggestion: `请使用 help 工具查看 ${dataPath ? '对应' : ''}数据规范。调用方式：help 无需参数，直接调用即可获取完整的工具使用说明和数据格式规范。`,
    dataPath,
    expectedFormat,
  };
}

function validateAction(action: ExecuteAction, index: number): ToolValidationError[] {
  const errors: ToolValidationError[] = [];
  const opLabel = `actions[${index}](${action.action}:${action.target})`;

  if (!action.action || !VALID_ACTION_TYPES.has(action.action)) {
    errors.push(makeError('INVALID_ACTION', `${opLabel} - 无效的操作类型 "${action.action}"，可选值：create | update | delete`, `actions[${index}].action`, '"create" | "update" | "delete"'));
    return errors;
  }

  const schema = findSchema(action.target);
  if (!schema) {
        errors.push(makeError('INVALID_TARGET', `${opLabel} - 不支持的数据类型 "${action.target}"，可选值：volume | chapter | character | faction | setting | item | outline | chapter_content`, `actions[${index}].target`, 'volume | chapter | character | faction | setting | item | outline | chapter_content'));
    return errors;
  }

  if (!schema.supportedActions.includes(action.action)) {
    const supported = schema.supportedActions.join(' | ');
    errors.push(makeError('UNSUPPORTED_ACTION', `${opLabel} - ${schema.label} 不支持 ${action.action} 操作，仅支持：${supported}`, `actions[${index}].action`, supported));
    return errors;
  }

  if ((action.action === 'delete' || action.action === 'update') && !action.targetId) {
    errors.push(makeError('MISSING_TARGET_ID', `${opLabel} - ${action.action === 'delete' ? '删除' : '修改'}操作必须提供 targetId（数据库记录的 UUID），请先通过 query 工具查询获取正确 ID`, `actions[${index}].targetId`, 'string (数据库记录 UUID)'));
  }

  if (action.action === 'create' && (!action.data || Object.keys(action.data).length === 0)) {
    errors.push(makeError('MISSING_DATA', `${opLabel} - 创建操作必须提供 data 字段`, `actions[${index}].data`, '对象，包含必要字段'));
    return errors;
  }

  if (action.data) {
    for (const field of schema.fields) {
      if (action.action === 'create' && field.requiredForCreate) {
        if (!(field.key in action.data) || action.data[field.key] === undefined || action.data[field.key] === null || action.data[field.key] === '') {
          errors.push(makeError('MISSING_REQUIRED_FIELD', `${opLabel} - 创建${schema.label}缺少必填字段 "data.${field.key}"（${field.description}）`, `actions[${index}].data.${field.key}`, field.type));
        }
      }
    }
  }

  return errors;
}

function detectConflicts(actions: ExecuteAction[]): ToolValidationError[] {
  const errors: ToolValidationError[] = [];
  const entityOpsMap = new Map<string, Array<{ action: string; index: number }>>();

  for (const [i, op] of actions.entries()) {
    if (op.targetId) {
      const key = `${op.target}:${op.targetId}`;
      const existing = entityOpsMap.get(key);
      if (existing) existing.push({ action: op.action, index: i + 1 });
      else entityOpsMap.set(key, [{ action: op.action, index: i + 1 }]);
    }
  }

  for (const [entityKey, ops] of entityOpsMap) {
    if (ops.length <= 1) continue;
    const actionTypes = ops.map((o) => o.action);
    const opNums = ops.map((o) => `操作[${o.index}]`).join('、');

    if (actionTypes.includes('delete') && actionTypes.includes('update')) {
      const del = ops.filter((o) => o.action === 'delete').map((o) => `操作[${o.index}]`).join('、');
      const upd = ops.filter((o) => o.action === 'update').map((o) => `操作[${o.index}]`).join('、');
      errors.push(makeError('CONFLICT_DELETE_UPDATE', `${entityKey}：不能同时删除（${del}）和修改（${upd}）同一条记录，请二选一`));
    }

    const delOps = ops.filter((o) => o.action === 'delete');
    if (delOps.length > 1) {
      errors.push(makeError('CONFLICT_DUPLICATE_DELETE', `${entityKey}：存在 ${delOps.length} 个重复删除操作（${opNums}），只需保留一个`));
    }
  }

  return errors;
}

export function validateExecuteActions(rawActions: unknown): SchedulerResult {
  let actions: ExecuteAction[];
  if (!rawActions) {
    actions = [];
  } else if (Array.isArray(rawActions)) {
    actions = rawActions as ExecuteAction[];
  } else if (typeof rawActions === 'object' && rawActions !== null) {
    const single = rawActions as Record<string, unknown>;
    if (single.action && single.target) {
      actions = [single as unknown as ExecuteAction];
    } else {
      const nested = single.actions;
      if (Array.isArray(nested)) {
        actions = nested as ExecuteAction[];
      } else if (typeof nested === 'object' && nested !== null) {
        const ns = nested as Record<string, unknown>;
        actions = ns.action && ns.target ? [ns as unknown as ExecuteAction] : [];
      } else {
        actions = [];
      }
    }
  } else {
    actions = [];
  }

  if (actions.length === 0) {
    return {
      valid: false,
      errors: [makeError('EMPTY_ACTIONS', 'execute 操作列表为空，请提供至少一个操作。格式：{"actions":[{"action":"create","target":"character","data":{"name":"张三"}}]}', 'actions', '数组，每项含 action、target、data（create 时必填）或 targetId（update/delete 时必填）')],
    };
  }

  const errors: ToolValidationError[] = [];
  for (const [i, action] of actions.entries()) {
    errors.push(...validateAction(action, i));
  }

  if (errors.length > 0) return { valid: false, errors };

  const conflictErrors = detectConflicts(actions);
  if (conflictErrors.length > 0) return { valid: false, errors: conflictErrors };

  return { valid: true, errors: [], normalizedActions: actions };
}

export function formatSchedulerErrors(result: SchedulerResult): string {
  if (result.valid) return '';
  const lines = ['【数据规范验证失败 — 请根据以下错误修正后重试】', ''];
  for (const [i, err] of result.errors.entries()) {
    lines.push(`${i + 1}. [${err.code}] ${err.message}`);
    if (err.dataPath) lines.push(`   字段路径: ${err.dataPath}`);
    if (err.expectedFormat) lines.push(`   期望格式: ${err.expectedFormat}`);
  }
  lines.push('');
  lines.push('💡 提示：如果对数据格式不确定，可以调用 help 工具获取完整的数据规范文档。');
  lines.push('   help 工具无需参数，直接调用即可。');
  return lines.join('\n');
}

export function getHelpContent(): string {
  const lines: string[] = [
    '# 小天书 AI 创作助手 — 工具使用说明',
    '',
    '## 概述',
    '本系统提供三个工具：`query`（查询）、`execute`（执行变更）、`help`（帮助）。',
    '所有数据操作遵循统一的接口规范，通过声明式数据格式完成增删改查。',
    '',
    '## 1. query 工具 — 查询创作数据（只读）',
    '',
    '### 参数',
    '- `scope`（字符串，可选）：查询范围。默认 "context"。',
    '  可选值：context | character | faction | setting | chapter | volume | item | outline | book | timeline | chapter_content',
    '- `filters`（对象，可选）：筛选条件',
    '  - 通用：{ id?, name?, title?, category?, volumeId?, limit? }',
    '  - scope="timeline" 时可传 { volumeId } 限定卷',
    '  - scope="chapter_content" 时必传 { id }（章节数据库 ID）',
    '- `detailLevel`（字符串，可选）：brief | normal（默认）| detailed',
    '',
    '### 使用建议',
    '- 优先使用 scope="context" 一次性获取完整创作上下文',
    '- 需要深入查看具体章节时用 scope="chapter_content" 传 filters.id',
    '- 需要理解剧情走向时用 scope="timeline"',
    '- 注意：章节结果中的 id 是数据库 ID（UUID），chapter_number 才是章节编号',
    '',
    '## 2. execute 工具 — 执行创作数据变更（需审批）',
    '',
    '### 参数',
    '- `actions`（必填）：操作列表，支持两种格式：',
    '  单对象格式：{ action: "create", target: "character", data: { name: "张三" } }',
    '  数组格式：[{ action: "create", target: "character", data: { name: "张三" } }]',
    '- `atomic`（布尔，可选）：是否原子操作（默认 false）',
    '- `description`（字符串，可选）：操作描述',
    '',
    '### 通用规则（所有实体类型适用）',
    '- create 必须提供 data，含实体必需字段',
    '- update 必须提供 targetId 和 data（仅含要更新的字段）',
    '- delete 必须提供 targetId',
    '- targetId 是数据库记录的 UUID（通过 query 获取），不是名称或编号',
    '- 所有变更操作会弹出审批标签，用户确认后写入数据库',
    '',
    '### 各实体数据格式',
    '',
  ];

  for (const schema of ENTITY_SCHEMAS) {
    lines.push(`### ${schema.label}（target="${schema.target}"）`);
    lines.push(`支持操作：${schema.supportedActions.join(' | ')}`);
    if (schema.notes) {
      for (const note of schema.notes) lines.push(`注意：${note}`);
    }
    lines.push('');
    lines.push('| 字段 | 类型 | 必填(create) | 说明 |');
    lines.push('|------|------|-------------|------|');
    for (const field of schema.fields) {
      lines.push(`| data.${field.key} | ${field.type} | ${field.requiredForCreate ? '是' : '否'} | ${field.description} |`);
    }
    lines.push('');
  }

lines.push('### 示例');
    lines.push('');
    lines.push('```json');
    lines.push('// 创建人物');
    lines.push('{"actions":{"action":"create","target":"character","data":{"name":"林轩","age":"20","strength":"筑基期","description":"主角"}}}');
    lines.push('');
    lines.push('// 创建章节');
    lines.push('{"actions":{"action":"create","target":"chapter","data":{"volume_id":"<uuid>","title":"第三章：初入江湖","content":"正文内容（可选）","summary":"本章摘要（可选）"}}}');
    lines.push('');
    lines.push('// 批量操作');
    lines.push('{"actions":[');
    lines.push('  {"action":"create","target":"character","data":{"name":"林轩","strength":"筑基期"}},');
    lines.push('  {"action":"update","target":"setting","targetId":"<uuid>","data":{"content":"更新后的设定内容"}}');
    lines.push('],"description":"创建主角并更新世界观设定"}');
  lines.push('');
  lines.push('// 更新章节内容');
  lines.push('{"actions":{"action":"update","target":"chapter_content","targetId":"<uuid>","data":{"content":"第五章正文内容...（完整正文）"}}}');
  lines.push('```');
  lines.push('');
  lines.push('## 3. help 工具 — 获取本帮助文档');
  lines.push('');
  lines.push('无需参数，直接调用即可获取完整的工具使用说明和数据规范。');
  lines.push('当不确定数据格式时，随时调用此工具。');

  return lines.join('\n');
}
